import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Coins, ChevronDown } from 'lucide-react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '';

interface CreditBalance {
  creditsRemaining: number;
  monthlyCredits: number;
  tier: string;
  percentage: number;
  isLowCredits: boolean;
  nextResetDate: string | null;
}

interface CurrentSubscriptionResponse {
  subscription: {
    tier: string;
    status: string;
    monthlyCredits: number;
    creditsRemaining: number;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  };
  balance: CreditBalance;
}

export default function CreditBalanceWidget() {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBalance();
    const interval = setInterval(fetchBalance, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchBalance = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setBalance(null);
        setLoading(false);
        return;
      }

      const response = await axios.get<CurrentSubscriptionResponse>(`${API_URL}/api/subscriptions/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Use balance from current subscription endpoint to ensure tier is synced
      setBalance(response.data.balance);
    } catch (error: any) {
      console.error('Error fetching credit balance:', error);
      // Handle 401 - user not authenticated, don't show widget
      if (error.response?.status === 401) {
        setBalance(null);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg animate-pulse">
        <div className="w-5 h-5 bg-gray-300 rounded"></div>
        <div className="w-12 h-4 bg-gray-300 rounded"></div>
      </div>
    );
  }

  // Don't render if no balance (not authenticated or error)
  if (!balance) {
    return null;
  }

  const getTierBadge = () => {
    switch (balance.tier) {
      case 'FREE': return { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Gratuit' };
      case 'BASIC': return { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Basic' };
      case 'PREMIUM': return { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Premium' };
      case 'UNLIMITED': return { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Gold' };
      default: return { bg: 'bg-gray-100', text: 'text-gray-700', label: balance.tier };
    }
  };

  const tierBadge = getTierBadge();

  return (
    <div className="relative">
      {/* Compact Button - mai mic pe mobil */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-all ${
          balance.isLowCredits 
            ? 'bg-red-50 hover:bg-red-100 border border-red-200' 
            : 'bg-blue-50 hover:bg-blue-100 border border-blue-200'
        }`}
      >
        <Coins className={`w-4 h-4 sm:w-5 sm:h-5 ${balance.isLowCredits ? 'text-red-600' : 'text-blue-600'}`} />
        <span className={`font-semibold text-xs sm:text-sm ${balance.isLowCredits ? 'text-red-700' : 'text-blue-700'}`}>
          {Number.isInteger(balance.creditsRemaining) ? balance.creditsRemaining : parseFloat(balance.creditsRemaining.toFixed(2))}
        </span>
        <ChevronDown className="hidden sm:block w-4 h-4 text-gray-400" />
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowDropdown(false)}
          />
          <div className="fixed left-2 right-2 sm:absolute sm:left-auto sm:right-0 sm:w-72 top-auto mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 py-3 px-4 z-50 max-w-sm mx-auto sm:mx-0" style={{ top: 'auto' }}>
            {/* Header */}
            <div className="flex items-center justify-between mb-3 pb-3 border-b">
              <h3 className="font-semibold text-gray-900">Credite FII FIT</h3>
              <span className={`text-xs px-2 py-1 rounded-full ${tierBadge.bg} ${tierBadge.text} font-medium`}>
                {tierBadge.label}
              </span>
            </div>

            {/* Balance */}
            <div className="mb-3">
              <div className="flex items-baseline gap-2 mb-2">
                <span className={`text-2xl font-bold ${balance.isLowCredits ? 'text-red-600' : 'text-blue-600'}`}>
                  {Number.isInteger(balance.creditsRemaining) ? balance.creditsRemaining : parseFloat(balance.creditsRemaining.toFixed(2))}
                </span>
                <span className="text-sm text-gray-500">/ {balance.monthlyCredits} credite</span>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all ${
                    balance.isLowCredits ? 'bg-red-500' : 
                    balance.percentage > 50 ? 'bg-green-500' : 'bg-yellow-500'
                  }`}
                  style={{ width: `${Math.min(balance.percentage, 100)}%` }}
                />
              </div>
            </div>

            {/* Warning */}
            {balance.isLowCredits && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-700 font-medium">
                  ⚠️ Credite reduse! Upgrade pentru mai multe credite.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-2">
              <button
                onClick={() => {
                  setShowDropdown(false);
                  navigate('/subscriptions');
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                {balance.tier === 'FREE' ? 'Upgrade Abonament' : 'Gestionează Abonament'}
              </button>
              <button
                onClick={() => {
                  setShowDropdown(false);
                  navigate('/extra-credits');
                }}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Cumpără Credite Extra
              </button>
            </div>

            {/* Next Reset */}
            {balance.nextResetDate && (
              <div className="mt-3 pt-3 border-t text-xs text-gray-500 text-center">
                Reset: {new Date(balance.nextResetDate).toLocaleDateString('ro-RO')}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
